import {
  Badge,
  Box,
  Button,
  Group,
  Modal,
  Select,
  Stack,
  Text,
  Textarea,
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useEffect, useState, type ReactNode } from 'react';
import type { Medication } from '../data';
import type { DoseSpotDemoCompletePayload, DoseSpotEmbedIntent } from './dosespotTypes';
import { DOSESPOT_PREVIEW_IMAGE_PATH } from './dosespotTypes';

export interface DoseSpotMedicationShellProps {
  opened: boolean;
  onClose: () => void;
  intent: DoseSpotEmbedIntent;
  /** Active medication for refill / discontinue; ignored for newRx */
  medication?: Medication | null;
  /**
   * When DoseSpot is wired, pass the iframe (or vendor widget) here.
   * Demo controls hide automatically when this is set.
   */
  embedSlot?: ReactNode;
  /** Override static preview image URL (defaults to {@link DOSESPOT_PREVIEW_IMAGE_PATH}). */
  previewImageSrc?: string;
  /**
   * Simulates a successful embed session (refill / discontinue).
   * Real integration: invoke the same handler from postMessage / SDK.
   */
  onDemoSessionComplete?: (payload: DoseSpotDemoCompletePayload) => void;
}

function intentTitle(intent: DoseSpotEmbedIntent): string {
  switch (intent) {
    case 'refill':
      return 'Request refill';
    case 'discontinue':
      return 'Mark inactive';
    default:
      return 'Add medication';
  }
}

function placeholderCopy(intent: DoseSpotEmbedIntent): { primary: string; secondary: string } {
  switch (intent) {
    case 'refill':
      return {
        primary: 'DoseSpot refill iframe will load here',
        secondary: 'Future embed: pharmacy selection, quantity, and refill submission.',
      };
    case 'discontinue':
      return {
        primary: 'DoseSpot discontinue / hold iframe will load here',
        secondary: 'Future embed: reason codes and inactive workflow per DoseSpot.',
      };
    default:
      return {
        primary: 'DoseSpot iframe will load here',
        secondary: 'Future ePrescribe embed area',
      };
  }
}

/**
 * Host shell for DoseSpot: same modal for new Rx, refill, and discontinue.
 * Pass `embedSlot` when integrating; until then placeholder + optional demo actions.
 */
export function DoseSpotMedicationShell({
  opened,
  onClose,
  intent,
  medication,
  embedSlot,
  previewImageSrc = DOSESPOT_PREVIEW_IMAGE_PATH,
  onDemoSessionComplete,
}: DoseSpotMedicationShellProps): JSX.Element {
  const isNarrow = useMediaQuery('(max-width: 48em)');
  const [inactiveStatus, setInactiveStatus] = useState<'Discontinued' | 'On Hold'>('Discontinued');
  const [inactiveNote, setInactiveNote] = useState('');
  const [previewLoadFailed, setPreviewLoadFailed] = useState(false);

  useEffect(() => {
    if (opened && intent === 'discontinue') {
      setInactiveStatus('Discontinued');
      setInactiveNote('');
    }
  }, [opened, intent, medication?.id]);

  useEffect(() => {
    if (opened) setPreviewLoadFailed(false);
  }, [opened, previewImageSrc]);

  const showDemoActions = !embedSlot && (intent === 'refill' || intent === 'discontinue');
  const { primary, secondary } = placeholderCopy(intent);
  const rxLabel = medication?.dosespotPrescriptionId
    ? `Rx ID: ${medication.dosespotPrescriptionId}`
    : medication
      ? 'Rx ID: (assign when DoseSpot is connected)'
      : null;

  const handleDemoRefill = () => {
    if (!medication) return;
    onDemoSessionComplete?.({ intent: 'refill', medicationId: medication.id });
  };

  const handleDemoDiscontinue = () => {
    if (!medication) return;
    onDemoSessionComplete?.({
      intent: 'discontinue',
      medicationId: medication.id,
      status: inactiveStatus,
      note: inactiveNote.trim() || undefined,
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        <Stack gap={6}>
          <Group gap="sm" align="center" wrap="wrap">
            <Text fw={600} size="lg">
              {intentTitle(intent)}
            </Text>
            <Badge variant="light" color="gray" size="sm">
              DoseSpot
            </Badge>
            <Text size="xs" c="dimmed" fs="italic">
              DoseSpot Shell
            </Text>
          </Group>
          {intent !== 'newRx' && medication && (
            <Stack gap={2}>
              <Text size="sm" c="dimmed" fw={500}>
                {medication.name} · {medication.dosage} · {medication.frequency}
              </Text>
              {rxLabel && (
                <Text size="xs" c="dimmed">
                  {rxLabel}
                </Text>
              )}
            </Stack>
          )}
          {intent === 'newRx' && (
            <Text size="sm" c="dimmed" fw={400}>
              Future ePrescribe embed — reserved for embedded prescribing.
            </Text>
          )}
        </Stack>
      }
      size="xl"
      centered
      fullScreen={isNarrow ?? false}
      styles={{
        content: {
          border: '1px solid #9CA3AF',
          maxWidth: 'min(1600px, 98vw)',
          width: isNarrow ? '98vw' : 'min(1600px, 98vw)',
          display: 'flex',
          flexDirection: 'column',
          minHeight: isNarrow ? 'min(560px, 82vh)' : 'min(720px, 85vh)',
          maxHeight: 'min(960px, 94vh)',
        },
        body: {
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          gap: 0,
          padding: 0,
          maxHeight: 'min(960px, 94vh)',
        },
      }}
    >
      <Box
        flex={1}
        miw={0}
        mih={0}
        style={{
          flex: 1,
          minHeight: isNarrow ? 'min(420px, 62vh)' : 'min(560px, 72vh)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          border:
            embedSlot || previewLoadFailed
              ? embedSlot
                ? '1px solid var(--mantine-color-gray-4)'
                : '1px dashed var(--mantine-color-gray-4)'
              : undefined,
          borderRadius: embedSlot || previewLoadFailed ? 'var(--mantine-radius-md)' : undefined,
          backgroundColor:
            embedSlot || previewLoadFailed ? 'var(--mantine-color-gray-0)' : undefined,
        }}
      >
        {embedSlot ? (
          <Box flex={1} miw={0} mih={0} style={{ minHeight: 0, overflow: 'auto' }}>
            {embedSlot}
          </Box>
        ) : previewLoadFailed ? (
          <Stack
            align="center"
            justify="center"
            gap="md"
            flex={1}
            p="xl"
            style={{ minHeight: 'inherit' }}
          >
            <Text fw={600} size="sm" ta="center">
              {primary}
            </Text>
            <Text size="sm" c="dimmed" ta="center" maw={480}>
              {secondary}
            </Text>
            <Text size="xs" c="dimmed" ta="center" maw={420}>
              Could not load preview image ({previewImageSrc}). Add the file under
              medplum-ui/public/integrations/ or pass previewImageSrc.
            </Text>
          </Stack>
        ) : (
          <Box flex={1} miw={0} mih={0} pos="relative" style={{ minHeight: 0, backgroundColor: 'var(--mantine-color-gray-1)' }}>
            <img
              src={previewImageSrc}
              alt="DoseSpot e-prescribing integration preview (static mockup)"
              onError={() => setPreviewLoadFailed(true)}
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                objectPosition: 'top center',
              }}
            />
          </Box>
        )}
      </Box>

      {showDemoActions && intent === 'discontinue' && (
        <Stack gap="sm" px="md" pt="md">
          <Select
            label="Inactive status (demo — usually returned by DoseSpot)"
            data={[
              { value: 'Discontinued', label: 'Discontinued' },
              { value: 'On Hold', label: 'On Hold' },
            ]}
            value={inactiveStatus}
            onChange={(v) => setInactiveStatus((v as 'Discontinued' | 'On Hold') ?? 'Discontinued')}
          />
          <Textarea
            label="Note (optional)"
            placeholder="Reason or instructions"
            value={inactiveNote}
            onChange={(e) => setInactiveNote(e.currentTarget.value)}
            minRows={2}
            autosize
          />
        </Stack>
      )}

      <Group
        justify="space-between"
        align="flex-end"
        wrap="wrap"
        px="md"
        py="md"
        style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }}
      >
        <Text size="xs" c="dimmed">
          {embedSlot
            ? 'Live embed'
            : previewLoadFailed
              ? 'Test shell — no backend or integration'
              : 'Static UI preview — DoseSpot embed pending'}
        </Text>
        <Group gap="sm">
          {showDemoActions && intent === 'refill' && (
            <Button onClick={handleDemoRefill} disabled={!medication}>
              Simulate refill submitted (demo)
            </Button>
          )}
          {showDemoActions && intent === 'discontinue' && (
            <Button onClick={handleDemoDiscontinue} disabled={!medication}>
              Apply inactive (demo)
            </Button>
          )}
          <Button variant="default" onClick={onClose}>
            Close
          </Button>
        </Group>
      </Group>
    </Modal>
  );
}
